// UNICO modulo di accesso ai dati del frontend (regola 5): nessuna fetch nei componenti.
// I tipi arrivano da api/openapi.yaml (npm run genera-tipi): se il frontend non rispetta
// il contratto, non compila.
import createClient from 'openapi-fetch';
import type { components, paths } from './schema';

export type Schemi = components['schemas'];

export const api = createClient<paths>({ baseUrl: '/api/v1', credentials: 'same-origin' });

/** Errore di una chiamata: stato HTTP (0 = rete non raggiungibile) e dettaglio dal backend (problem+json). */
export class ErroreApi extends Error {
  constructor(
    readonly stato: number,
    readonly problema?: Partial<Schemi['Problema']>,
  ) {
    super(problema?.detail ?? problema?.title ?? `Errore ${stato}`);
  }

  /** Messaggio per il campo indicato, se il backend ha segnalato errori per campo. */
  erroreCampo(campo: string): string | undefined {
    return this.problema?.errori?.find((e) => e.campo === campo)?.messaggio;
  }
}

/**
 * Indirizzo del PDF di un documento del cliente (scaricaPdfDocumento, D44): si apre con un normale link, perché il
 * browser (anche su iPhone) mostra il PDF da solo e la sessione viaggia nel cookie. È l'unico indirizzo delle API
 * costruito a mano: resta qui, nel modulo di accesso ai dati (regola 5).
 */
export function urlPdfDocumento(ditta: string, account: string, documento: string): string {
  return `/api/v1/ditte/${encodeURIComponent(ditta)}/account/${encodeURIComponent(account)}/documenti/${encodeURIComponent(documento)}/pdf`;
}

/** Indirizzo dell'immagine di una foto (D52), intera o in miniatura: per <img src>, con il cookie di sessione. */
export function urlFotoFile(ditta: string, foto: string, miniatura = false): string {
  return `/api/v1/ditte/${encodeURIComponent(ditta)}/foto/${encodeURIComponent(foto)}/file${miniatura ? '?miniatura=true' : ''}`;
}

/** Indirizzo della Guida (D74, D76): la serve il backend con la sessione, nella versione del ruolo; `capitolo` è l'ancora. */
export function urlGuida(ditta: string, capitolo?: string): string {
  return `/api/v1/ditte/${encodeURIComponent(ditta)}/guida${capitolo ? `#${capitolo}` : ''}`;
}

/** Chiamato quando la sessione non è più valida (il router porta al login). */
let suSessioneScaduta: () => void = () => {};
export function impostaSuSessioneScaduta(f: () => void): void {
  suSessioneScaduta = f;
}

/**
 * Esegue una chiamata di `api` e restituisce i dati, oppure lancia ErroreApi.
 * Es.: const s = await esegui(api.GET('/auth/sessione'));
 */
export async function esegui<T>(
  chiamata: Promise<{ data?: T; error?: unknown; response: Response }>,
  opzioni: { ignora401?: boolean } = {},
): Promise<T> {
  let r: { data?: T; error?: unknown; response: Response };
  try {
    r = await chiamata;
  } catch {
    throw new ErroreApi(0, { title: 'Rete non disponibile', detail: 'Il server non risponde: controlla la connessione e riprova.' });
  }
  if (!r.response.ok) {
    if (r.response.status === 401 && !opzioni.ignora401) suSessioneScaduta();
    throw new ErroreApi(r.response.status, r.error as Partial<Schemi['Problema']> | undefined);
  }
  return r.data as T;
}
